import React, { useState } from 'react';
import { Alert, Button, Card, ProgressBar } from 'react-bootstrap';
import { ModulosService } from '../../../../service/modulos.service';
import { Modulo } from '../../../../service/types/cursos.types';
import PaginaRenderer from './PaginaRenderer';

interface ModuloPlayerProps {
  modulo: Modulo;
  onCompletado: (modulosCompletos: boolean) => void;
  onCerrar: () => void;
}

export default function ModuloPlayer({ modulo, onCompletado, onCerrar }: ModuloPlayerProps) {
  const paginas = [...(modulo.paginas ?? [])].sort((a, b) => a.orden - b.orden);
  const [index, setIndex] = useState(0);
  const [testsAprobados, setTestsAprobados] = useState<Record<string, boolean>>({});
  const [completando, setCompletando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const paginaActual = paginas[index];
  const esUltima = index === paginas.length - 1;
  const puedeAvanzar = paginaActual?.tipo !== 'test' || !!testsAprobados[paginaActual.id];

  const handleSiguiente = () => setIndex((i) => Math.min(i + 1, paginas.length - 1));
  const handleAnterior = () => setIndex((i) => Math.max(i - 1, 0));

  const handleCompletar = async () => {
    setCompletando(true);
    setError(null);
    try {
      const resultado = await ModulosService.completar(modulo.id);
      onCompletado(resultado.modulosCompletos);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'No se pudo completar el módulo. Verifique que haya aprobado todos los tests.');
    } finally {
      setCompletando(false);
    }
  };

  if (!paginaActual) {
    return (
      <Card>
        <Card.Body>
          <p className="text-muted">Este módulo no tiene páginas.</p>
          <Button variant="secondary" onClick={onCerrar}>
            Volver
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>{modulo.titulo}</span>
        <Button size="sm" variant="outline-secondary" onClick={onCerrar}>
          Volver al curso
        </Button>
      </Card.Header>
      <Card.Body>
        <ProgressBar
          className="mb-3"
          now={((index + 1) / paginas.length) * 100}
          label={`${index + 1} / ${paginas.length}`}
        />
        <PaginaRenderer
          pagina={paginaActual}
          onTestAprobado={() => setTestsAprobados((prev) => ({ ...prev, [paginaActual.id]: true }))}
        />

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={handleAnterior} disabled={index === 0}>
            Anterior
          </Button>
          {esUltima ? (
            <Button variant="success" onClick={handleCompletar} disabled={!puedeAvanzar || completando}>
              {completando ? 'Completando...' : 'Completar módulo'}
            </Button>
          ) : (
            <Button variant="primary" onClick={handleSiguiente} disabled={!puedeAvanzar}>
              Siguiente
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
